import { createResource, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, Check, Heart, Inbox, Loader2, Package } from "lucide-solid";

import { Link, useNavigate } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { burstFrom, pressable } from "@/lib/motion";
import { useI18n } from "@/providers/i18n-provider";
import { addToCollectionMutation, type CollectionState } from "@famidex/shared";
import { fetchCatalogProduct, fetchProductParts } from "../data/catalog-rest";

/**
 * 🎯 Ficha del producto + añadir a colección. Datos del catálogo por REST
 * (guest-first); la escritura va por `addToCollectionMutation` (UPDATE-or-INSERT
 * local-first, gotcha #15). El invitado que intenta añadir se enruta a login
 * (guest-first para OJEAR, cuenta para POSEER). Traducción del diseño Stitch
 * `detalle_del_producto`.
 */
export function ProductDetailPage(props: { productId: string }) {
  const navigate = useNavigate();
  const session = useSession();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const [product] = createResource(
    () => props.productId,
    (id: string) => fetchCatalogProduct(id),
  );
  const [parts] = createResource(
    () => props.productId,
    (id: string) => fetchProductParts(id),
  );

  const [busy, setBusy] = createSignal(false);
  const [added, setAdded] = createSignal<CollectionState | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  // CTA «Añadir a colección»: ancla de la celebración (burst de partículas
  // GPU-only, lib/motion). El overlay del burst sobrevive a la navegación.
  let addButtonEl: HTMLButtonElement | undefined;

  const handleAdd = async (state: CollectionState) => {
    setError(null);
    // Guest-first para OJEAR; poseer requiere cuenta.
    if (!session.isAuthenticated()) {
      void navigate({ to: "/login" });
      return;
    }
    const db = drizzle();
    const userId = session.userId();
    if (!db || !userId) {
      return;
    }
    setBusy(true);
    try {
      await addToCollectionMutation(db, userId, props.productId, { state });
      setAdded(state);
      // 🎉 Celebración: burst de partículas desde el CTA (transform/opacity,
      // se auto-limpia) + tick háptico. Reduced-motion la omite.
      if (state === "owned" && addButtonEl) {
        burstFrom(addButtonEl, { hapticMs: 12 });
      }
      // Aterriza en Mi Colección para ver el ítem recién añadido.
      void navigate({ to: "/collection" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="flex flex-col gap-6" data-testid="product-detail">
      <Link
        to="/catalog"
        class="inline-flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft class="h-4 w-4" />
        {t(msg`Catálogo`)}
      </Link>

      <Show
        when={!product.loading}
        fallback={
          <div class="grid place-items-center py-20 text-muted-foreground">
            <Loader2 class="h-8 w-8 animate-spin" />
          </div>
        }
      >
        <Show
          when={product()}
          fallback={
            <div class="grid place-items-center gap-2 py-20 text-center text-muted-foreground">
              <Package class="h-8 w-8" />
              <p class="text-sm">{t(msg`Este producto no está en el catálogo.`)}</p>
            </div>
          }
        >
          {(prod) => (
            <>
              {/* Imagen hero */}
              <div class="relative overflow-hidden rounded-2xl border border-border/60 bg-muted">
                <Show
                  when={prod().imageUrl}
                  fallback={
                    <div class="grid aspect-[4/3] w-full place-items-center text-muted-foreground/40">
                      <Package class="h-16 w-16" />
                    </div>
                  }
                >
                  <img
                    src={prod().imageUrl ?? ""}
                    alt={prod().editionName}
                    class="aspect-[4/3] w-full object-cover"
                  />
                </Show>
                <Show when={prod().isLimited}>
                  <span class="absolute left-4 top-4 rotate-[-2deg] rounded-full bg-destructive px-3 py-1 text-xs font-bold uppercase tracking-widest text-destructive-foreground shadow-lg">
                    {t(msg`Coleccionable raro`)}
                  </span>
                </Show>
              </div>

              {/* Info */}
              <div class="rounded-xl border border-border/60 bg-card/70 p-5">
                <Show when={prod().category}>
                  <p class="text-xs font-bold uppercase tracking-widest text-danger">
                    {prod().category}
                  </p>
                </Show>
                <h1 class="mt-1 text-2xl font-semibold text-foreground">{prod().editionName}</h1>
                <Show when={prod().releaseYear}>
                  <p class="mt-1 text-sm text-muted-foreground">
                    {t(msg`Lanzamiento`)}: {prod().releaseYear}
                  </p>
                </Show>
                <Show when={prod().description}>
                  <p class="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {prod().description}
                  </p>
                </Show>
              </div>

              {/* Piezas del set */}
              <Show when={(parts() ?? []).length > 0}>
                <div class="rounded-xl border border-border/60 bg-card/50 p-5">
                  <h2 class="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">
                    {t(msg`Piezas del set`)}
                  </h2>
                  <ul class="flex flex-col gap-2">
                    <For each={parts()}>
                      {(part) => (
                        <li class="flex items-center gap-2 text-sm text-foreground">
                          <Check class="h-4 w-4 shrink-0 text-primary" />
                          <span>{part.name}</span>
                          <Show when={part.kind}>
                            <span class="text-xs text-muted-foreground">({part.kind})</span>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>

              {/* CTAs */}
              <div class="flex flex-col gap-3">
                <button
                  type="button"
                  data-testid="add-to-collection"
                  disabled={busy()}
                  onClick={() => void handleAdd("owned")}
                  ref={(el: HTMLButtonElement) => {
                    addButtonEl = el;
                    pressable(el, { scale: 0.97, hapticMs: 5 });
                  }}
                  class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-base font-semibold text-primary-foreground shadow-md disabled:opacity-60"
                >
                  <Show when={added() === "owned"} fallback={<Inbox class="h-5 w-5" />}>
                    <Check class="h-5 w-5" />
                  </Show>
                  {added() === "owned" ? t(msg`¡Añadido!`) : t(msg`Añadir a colección`)}
                </button>
                <button
                  type="button"
                  data-testid="add-to-wishlist"
                  disabled={busy()}
                  onClick={() => void handleAdd("wishlist")}
                  ref={(el: HTMLButtonElement) => pressable(el, { scale: 0.97, hapticMs: 5 })}
                  class="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary bg-transparent py-3.5 text-base font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-60"
                >
                  <Heart class="h-5 w-5" />
                  {t(msg`Añadir a deseos`)}
                </button>
                <Show when={error()}>
                  <p class="text-center text-sm text-destructive">{error()}</p>
                </Show>
              </div>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
}
